import { useStyles } from './styles'
import { DefaultDailyBonusRow } from '@/features/dailyBonusRow/default'
import { $userLanguage, $userToken, authUserFx } from '@/entities/user'
import { useUnit } from 'effector-react'
import { lang } from '@/shared/constants/lang'
import { BackgroundShading } from '@/shared/ui/backgroundShading'
import { Icon } from '@/shared/ui/icon'
import { useEffect, useMemo, useState } from 'react'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { useHideNav } from '@/shared/hooks/useHideNav'
import { DailyBonusLocker } from '@/shared/ui/dailyBonusLocker'

import { $dailyBonusDay, $dailyBonusRewards, $dailyBonusUnlock } from '@/entities/dailyBonus/model/store'
import { Spiner } from '@/shared/ui/spiner'
import { useTimer } from '@/shared/hooks/useTimer'
import { TimeDisplay } from '@/shared/ui/timeDisplay'
import { getDailyBonusInfoFx } from '@/entities/dailyBonus/model/effects'
import { environment } from '@/shared/constants'
import WebApp from '@twa-dev/sdk'

export const DefaultDailyBonus = () => {
  useHideNav()

  const { classes } = useStyles()
  const userToken = useUnit($userToken)
  const userLang = useUnit($userLanguage)
  const texts = lang[userLang].pages.dailyBonus

  const currentDay = useUnit($dailyBonusDay)
  const dailyBonusRewards = useUnit($dailyBonusRewards)
  const bonusUnlockingAt = useUnit($dailyBonusUnlock)

  useEffect(() => {
    if (!userToken) {
      authUserFx({ init_data: WebApp.initData })
      return
    }
    if (!dailyBonusRewards) {
      getDailyBonusInfoFx(userToken)
    }
  }, [dailyBonusRewards, userToken])

  const [claimUntill, setClaimUntill] = useState<string | null>(null)

  const { timeLeft: timeToClaimCurrent, canUpdate: canClaimCurrent } = useTimer(bonusUnlockingAt)
  const { timeLeft: timeToClaimNext, canUpdate: canClaimNext } = useTimer(claimUntill, null, !canClaimCurrent)

  useEffect(() => {
    if (canClaimCurrent && userToken && bonusUnlockingAt) {
      const now = new Date(bonusUnlockingAt)
      if (environment === 'dev') {
        const devCooldown = new Date(now.getTime() + 21 * 1000)
        setTimeout(() => setClaimUntill(devCooldown.toISOString()), 1000)
      } else {
        const prodCooldown = new Date(now.getTime() + 24 * 60 * 60 * 1000)
        setClaimUntill(prodCooldown.toISOString())
      }
    }
  }, [canClaimCurrent, userToken, bonusUnlockingAt])

  useEffect(() => {
    if ((canClaimCurrent || canClaimNext) && userToken) setTimeout(() => getDailyBonusInfoFx(userToken), 1000)
  }, [canClaimCurrent, userToken, canClaimNext])

  const activeDay = useMemo(() => {
    if (!dailyBonusRewards) return currentDay

    const _actDay = [...dailyBonusRewards]
      .reverse()
      .find(([baseBonus, premiumBonus]) => baseBonus.can_claim || premiumBonus.can_claim)
    if (_actDay) {
      if (_actDay[0].day && canClaimCurrent) return _actDay[0].day
      if (_actDay[0].day === 7 && canClaimNext) return 1
      return currentDay
    }
    return currentDay
  }, [dailyBonusRewards, currentDay, canClaimCurrent, canClaimNext])

  const noLocker = useMemo(() => {
    if (!dailyBonusRewards) return true
    if (currentDay === 7) {
      if (canClaimNext) {
        return false
      }
      return true
    }
    return false
  }, [dailyBonusRewards, currentDay, canClaimNext])

  const DEFAULT_GAP = 184
  const WRAPPER_HEIGHT = 143

  const lockerMargin = DEFAULT_GAP + WRAPPER_HEIGHT * activeDay
  useBackButton({})

  if (!dailyBonusRewards || !bonusUnlockingAt) return <Spiner variant="secondary" />

  return (
    <div className={classes.root}>
      <div
        className={classes.lockerWrapper}
        style={{
          transform: `translateY(${lockerMargin}px)`,
        }}
      >
        {!noLocker && <DailyBonusLocker />}
      </div>
      <div className={classes.timeDisplayWrapper}>
        <span className={classes.nextUnlockText}>
          {canClaimCurrent ? texts.default.claim : texts.default.nextUnlock}
        </span>

        {!canClaimCurrent ? <TimeDisplay timeLeft={timeToClaimCurrent} /> : <TimeDisplay timeLeft={timeToClaimNext} />}
      </div>
      <div className={classes.bgWrapper}>
        <img
          src="/assets/backgrounds/LeaderboardBG.webp"
          alt=""
          className={classes.background}
        />
        <div className={classes.textWrapper}>
          <h1 className={classes.heading}>{texts.default.heading}</h1>
          <h2 className={classes.subHeading}>{texts.default.subHeading}</h2>
        </div>
        <BackgroundShading
          height="120px"
          top="14px"
        />
      </div>
      <BackgroundShading
        height="52px"
        top="200px"
        upsideDown
      />

      <div className={classes.rewardsTextWrapper}>
        <div className={classes.rewardTextAndIcon}>
          <Icon
            view="plusLogo"
            width={55}
          />
          <span className={classes.rewardsText}>{texts.default.plusRewards}</span>
        </div>
        <span>
          <span className={classes.rewardsText}>{texts.default.freeRewards}</span>
        </span>
      </div>
      <div>
        {dailyBonusRewards.map((reward) => {
          const prevRewards = dailyBonusRewards[reward[0].day - 1]
          return (
            <DefaultDailyBonusRow
              key={reward[0].day}
              timeLeft={timeToClaimCurrent}
              canClaim={canClaimNext}
              currentDay={currentDay}
              prevRewards={prevRewards ? prevRewards : undefined}
              bonusUnlockingAt={bonusUnlockingAt}
              defaultReward={reward[0]}
              premiumReward={reward[1]}
            />
          )
        })}
      </div>
    </div>
  )
}
